// The loop card (click on a loop in the River): author, play time, licence, Wizard share,
// solo preview and Report (admins: remove it now).
import { RateSong } from "../ui/Stars";
import { useEffect, useState } from "react";
import { attribution, KITS, keyName, type LineupEntry, type LoopData, type PeriodDef } from "@eb/shared";
import { t } from "../i18n";
import { absoluteUrl, Link } from "../lib/router";
import { Modal } from "../ui/Modal";
import { WizardShareBar } from "../ui/WizardShare";
import { ReportDialog } from "../ui/ReportDialog";
import { RemoveSongButton } from "../ui/RemoveSong";
import { useSession } from "../session";
import { AddToTapeButton } from "../tapes/AddToTape";
import { MiniNotes } from "./MiniNotes";
import type { RiverPlayer } from "./player";
import { formatPlayTime, type SlotRef } from "./slots";

export function LoopCardModal({ player, slot, onClose }: { player: RiverPlayer; slot: SlotRef; onClose: () => void }) {
  const [entry, setEntry] = useState<LineupEntry | null>(null);
  const [loop, setLoop] = useState<LoopData | undefined>();
  const [period, setPeriod] = useState<PeriodDef | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [reporting, setReporting] = useState(false);
  const isAdmin = useSession((s) => s.user?.isAdmin === true);
  useEffect(() => {
    let alive = true;
    void (async () => {
      const [l, p] = await Promise.all([player.lineup(slot.day), player.period(slot.day)]);
      if (!alive) return;
      const e = l.entries[slot.slot];
      setEntry(e);
      setPeriod(p);
      const data = await player.loop(e.loopId);
      if (alive) setLoop(data);
    })();
    return () => {
      alive = false;
    };
  }, [player, slot]);

  const author = entry ? (entry.author ?? t("river.formerContributor")) : "…";
  const played = slot.startMs < player.worldNow();
  const human = entry?.kind === "human" && entry.author;

  async function togglePreview() {
    if (previewing) {
      await player.endPreview();
      setPreviewing(false);
    } else if (loop) {
      await player.preview(slot.day, loop);
      setPreviewing(true);
    }
  }

  function close() {
    if (previewing) void player.endPreview();
    onClose();
  }

  if (reporting && entry) return <ReportDialog loopId={entry.loopId} onClose={() => setReporting(false)} />;
  return (
    <Modal title={entry?.title ?? `Song #${slot.slot + 1}`} onClose={close}>
      <div className="stack">
        <p className="card-by">
          {t("card.by", { author: "" })}
          {human ? <Link href={`/u/${encodeURIComponent(entry.author!)}`}>{author}</Link> : <strong>{author}</strong>}
        </p>
        <MiniNotes loop={loop} width={440} height={80} label={`Notes of the song by ${author}`} />
        {entry?.kind === "human" && <RateSong loopId={entry.loopId} author={author} />}
        <p>{t(played ? "card.played" : "card.plays", { time: formatPlayTime(slot.startMs) })}</p>
        {period && (
          <p className="row">
            <span className="chip">{keyName(period.key)}</span>
            <span className="chip">{KITS[period.kit].name}</span>
            <span className="chip">{KITS[period.kit].genre}</span>
          </p>
        )}
        {entry && <WizardShareBar share={entry.wizardShare} filler={entry.kind === "filler"} />}
        <p className="muted small">
          {t("card.licence", {
            credit: entry
              ? attribution({
                  title: entry.title,
                  author: entry.author,
                  kind: entry.kind,
                  url: absoluteUrl(`/loop/${entry.loopId}`),
                })
              : "…",
          })}
        </p>
        <div className="row">
          <button type="button" className="btn" onClick={() => void togglePreview()} disabled={!loop}>
            {previewing ? t("card.stopPreview") : t("card.preview")}
          </button>
          {entry && entry.kind === "human" && (
            <Link className="btn btn-ghost" href={`/loop/${encodeURIComponent(entry.loopId)}`}>
              {t("card.open")}
            </Link>
          )}
          {entry && <AddToTapeButton loopId={entry.loopId} />}
          {entry?.kind === "human" && (
            <button type="button" className="btn btn-ghost" onClick={() => setReporting(true)} data-testid="report">
              {t("card.report")}
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={close}>
            {t("card.close")}
          </button>
        </div>
        {isAdmin && entry?.kind === "human" && <RemoveSongButton loopId={entry.loopId} />}
      </div>
    </Modal>
  );
}
